import React, { useEffect, useMemo, useState } from "react";
import Header from "../general/Header";
import Aside from "../general/Aside";
import Footer from "../general/Footer";
import axios from '../../axios/axiosconf';
import Loading from "../general/Loading";
import Unauthorized from "../general/Unauthorized";
import { Link } from "react-router-dom";
import { formatDateOnly } from '../superadmin/superadminDateUtils';
import { useClientPagination } from "./useClientPagination";
import ReportPaginationFooter from "./ReportPaginationFooter";
import { descargarExcelXlsx, descargarCsvReporte } from '../../../utils/reporteExportHelpers';

function tienePermiso(userdata) {
    const slugs = ['ver_listado_facturas', 'verReportes'];
    if (userdata.permissions && Array.isArray(userdata.permissions)) {
        return userdata.permissions.some((p) => slugs.includes(p.slug));
    }
    if (userdata.up) {
        return slugs.some((s) => userdata.up.includes(s));
    }
    return false;
}

function ReporteNotasCredito() {
    const [loggedIn, setLoggedIn] = useState(false);
    const [hasPermission, setHasPermission] = useState(false);
    const [client, setClient] = useState([]);
    const [loading, setLoading] = useState(false);
    const [notas, setNotas] = useState([]);
    const [resumenApi, setResumenApi] = useState(null);
    const [usuarios, setUsuarios] = useState([]);

    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [usuarioFiltro, setUsuarioFiltro] = useState('');
    const [tipoFiltro, setTipoFiltro] = useState('');
    const [busquedaPaciente, setBusquedaPaciente] = useState('');
    const [busquedaDocumento, setBusquedaDocumento] = useState('');
    const [numeroNc, setNumeroNc] = useState('');
    const [numeroFactura, setNumeroFactura] = useState('');
    const [historicoCompleto, setHistoricoCompleto] = useState(false);

    useEffect(() => {
        if (!localStorage.getItem('userdata')) {
            setHasPermission(false);
            setLoggedIn(true);
            return;
        }
        const userdata = JSON.parse(localStorage.getItem('userdata'));
        if (!tienePermiso(userdata)) {
            setHasPermission(false);
            setLoggedIn(true);
            return;
        }
        setHasPermission(true);

        const hoy = new Date();
        const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        const finMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
        setFechaInicio(inicioMes.toISOString().split('T')[0]);
        setFechaFin(finMes.toISOString().split('T')[0]);

        axios.get('/get-client?uid=' + userdata.user_id)
            .then((r) => { if (r.status === 200) setClient(r.data); setLoggedIn(true); })
            .catch(() => setLoggedIn(true));

        axios.get('/users-by-client?uid=' + userdata.user_id)
            .then((r) => { if (r.status === 200) setUsuarios(r.data); })
            .catch(() => setUsuarios([]));
    }, []);

    useEffect(() => {
        if (loggedIn && hasPermission && (historicoCompleto || (fechaInicio && fechaFin))) {
            cargarReporte();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loggedIn, hasPermission, historicoCompleto, fechaInicio, fechaFin, usuarioFiltro, tipoFiltro]);

    const cargarReporte = () => {
        if (!historicoCompleto && (!fechaInicio || !fechaFin)) {
            alert('Seleccione fecha inicio y fecha fin, o active "Histórico completo".');
            return;
        }
        setLoading(true);
        const userdata = JSON.parse(localStorage.getItem('userdata'));
        const params = {
            uid: userdata.user_id,
            fecha_inicio: historicoCompleto ? '1990-01-01' : fechaInicio,
            fecha_fin: historicoCompleto ? '3000-12-31' : fechaFin,
            usuario_id: usuarioFiltro || null,
            tipo: tipoFiltro || null,
            busqueda_paciente: busquedaPaciente || null,
            busqueda_documento: busquedaDocumento || null,
            numero_nc: numeroNc || null,
            numero_factura: numeroFactura || null,
        };

        axios.get('/reporte-notas-credito', { params })
            .then((response) => {
                const data = response.data || {};
                setNotas(Array.isArray(data.notas) ? data.notas : []);
                setResumenApi(data.resumen || null);
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
                setNotas([]);
                setResumenApi(null);
                setLoading(false);
            });
    };

    const handleLimpiarFiltros = () => {
        const hoy = new Date();
        const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        const finMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
        setFechaInicio(inicioMes.toISOString().split('T')[0]);
        setFechaFin(finMes.toISOString().split('T')[0]);
        setUsuarioFiltro('');
        setTipoFiltro('');
        setBusquedaPaciente('');
        setBusquedaDocumento('');
        setNumeroNc('');
        setNumeroFactura('');
        setHistoricoCompleto(false);
        setTimeout(() => cargarReporte(), 100);
    };

    const getTipoTexto = (tipo) => {
        if (tipo === 'electronica') return 'Electrónica';
        if (tipo === 'no_electronica') return 'No electrónica';
        return tipo || 'N/A';
    };

    const getNombreUsuario = (nota) => {
        if (nota.usuario_nombre && String(nota.usuario_nombre).trim()) {
            return nota.usuario_nombre.trim();
        }
        const u = usuarios.find((x) => x.id === nota.users_id);
        return u ? `${u.name || ''} ${u.lastname || ''}`.trim() : 'N/A';
    };

    const totalesResumen = useMemo(() => {
        if (resumenApi) {
            return {
                conteo: resumenApi.cantidad || 0,
                valor: resumenApi.suma_valor || 0,
                electronicas: resumenApi.cantidad_electronicas || 0,
                noElectronicas: resumenApi.cantidad_no_electronicas || 0,
            };
        }
        let valor = 0;
        let electronicas = 0;
        let noElectronicas = 0;
        notas.forEach((n) => {
            valor += parseFloat(n.valor_total || 0);
            if (n.tipo === 'electronica') electronicas++;
            else if (n.tipo === 'no_electronica') noElectronicas++;
        });
        return { conteo: notas.length, valor, electronicas, noElectronicas };
    }, [notas, resumenApi]);

    const pagination = useClientPagination(notas);

    const formatearMoneda = (valor) =>
        '$' + Number(valor || 0).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

    const filasExcel = () => notas.map((n) => ({
        'N° NC': n.numero_completo || n.numero_nc || '',
        'Tipo': getTipoTexto(n.tipo),
        'Fecha': formatDateOnly(n.fecha_emision),
        'Factura': n.factura_numero_completo || '',
        'Paciente': n.paciente_nombre || '',
        'Documento': n.paciente_documento || '',
        'Usuario': getNombreUsuario(n),
        'Motivo': n.motivo_observacion || '',
        'Valor bruto': parseFloat(n.valor_bruto || 0),
        'Valor total': parseFloat(n.valor_total || 0),
        'UUID': n.uuid || '',
    }));

    const exportarExcel = () => {
        if (notas.length === 0) {
            alert('No hay datos para exportar');
            return;
        }
        descargarExcelXlsx(
            filasExcel(),
            'NotasCredito',
            `reporte_notas_credito_${new Date().toISOString().split('T')[0]}.xlsx`
        );
    };

    const exportarCSV = () => {
        if (notas.length === 0) {
            alert('No hay datos para exportar');
            return;
        }
        descargarCsvReporte(filasExcel(), `reporte_notas_credito_${new Date().toISOString().split('T')[0]}.csv`);
    };

    if (hasPermission === false) return <Unauthorized />;
    if (!loggedIn) return <Loading />;

    return (
        <div className="hold-transition" style={{ fontSize: '13px' }}>
            <Header client={client} />
            <Aside client={client} activelink="reports" />
            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-12">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item"><Link to="/home">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/estadisticas">Reportes</Link></li>
                                    <li className="breadcrumb-item"><Link to="/estadisticas-contables">Contables</Link></li>
                                    <li className="breadcrumb-item active">Notas crédito</li>
                                </ol>
                            </div>
                        </div>
                        <h1 className="m-0">Reporte de Notas Crédito</h1>
                    </div>
                </div>

                <section className="content">
                    <div className="container-fluid">
                        <div className="card report-fin-card">
                            <div className="card-header"><h3 className="card-title">Filtros de búsqueda</h3></div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-2">
                                        <label>Fecha inicio</label>
                                        <input type="date" className="form-control form-control-sm" value={fechaInicio} disabled={historicoCompleto} onChange={(e) => setFechaInicio(e.target.value)} />
                                    </div>
                                    <div className="col-md-2">
                                        <label>Fecha fin</label>
                                        <input type="date" className="form-control form-control-sm" value={fechaFin} disabled={historicoCompleto} onChange={(e) => setFechaFin(e.target.value)} />
                                    </div>
                                    <div className="col-md-2">
                                        <label>Usuario</label>
                                        <select className="form-control form-control-sm" value={usuarioFiltro} onChange={(e) => setUsuarioFiltro(e.target.value)}>
                                            <option value="">Todos</option>
                                            {usuarios.map((u) => <option key={u.id} value={u.id}>{u.name} {u.lastname}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <label>Tipo</label>
                                        <select className="form-control form-control-sm" value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)}>
                                            <option value="">Todas</option>
                                            <option value="electronica">Electrónica</option>
                                            <option value="no_electronica">No electrónica</option>
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <label>N° NC</label>
                                        <input type="text" className="form-control form-control-sm" placeholder="Prefijo o número..." value={numeroNc} onChange={(e) => setNumeroNc(e.target.value)} />
                                    </div>
                                    <div className="col-md-2">
                                        <label>N° Factura</label>
                                        <input type="text" className="form-control form-control-sm" placeholder="Prefijo o número..." value={numeroFactura} onChange={(e) => setNumeroFactura(e.target.value)} />
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-md-4">
                                        <label>Paciente</label>
                                        <input type="text" className="form-control form-control-sm" placeholder="Nombre..." value={busquedaPaciente} onChange={(e) => setBusquedaPaciente(e.target.value)} />
                                    </div>
                                    <div className="col-md-3">
                                        <label>Documento paciente</label>
                                        <input type="text" className="form-control form-control-sm" placeholder="N° documento..." value={busquedaDocumento} onChange={(e) => setBusquedaDocumento(e.target.value)} />
                                    </div>
                                    <div className="col-md-5">
                                        <div className="form-check mt-4">
                                            <input className="form-check-input" type="checkbox" id="historicoNc" checked={historicoCompleto} onChange={(e) => setHistoricoCompleto(e.target.checked)} />
                                            <label className="form-check-label" htmlFor="historicoNc">Histórico completo (sin límite de fechas)</label>
                                        </div>
                                    </div>
                                </div>
                                <div className="row mt-3">
                                    <div className="col-md-12">
                                        <button type="button" className="btn btn-primary btn-sm mr-2" onClick={cargarReporte} disabled={loading}>
                                            <i className="fas fa-search"></i> Buscar
                                            {loading && <span className="spinner-border spinner-border-sm ml-2"></span>}
                                        </button>
                                        <button type="button" className="btn btn-secondary btn-sm mr-2" onClick={handleLimpiarFiltros}>
                                            <i className="fas fa-eraser"></i> Limpiar filtros
                                        </button>
                                        <button type="button" className="btn btn-success btn-sm mr-2" onClick={exportarExcel} disabled={notas.length === 0}>
                                            <i className="fas fa-file-excel"></i> Excel
                                        </button>
                                        <button type="button" className="btn btn-info btn-sm" onClick={exportarCSV} disabled={notas.length === 0}>
                                            <i className="fas fa-file-csv"></i> CSV
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-3">
                                <div className="card card-outline card-secondary">
                                    <div className="card-body">
                                        <h3 className="mb-0">{totalesResumen.conteo}</h3>
                                        <p className="text-muted mb-0 small">Notas crédito</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card card-outline card-info">
                                    <div className="card-body">
                                        <h3 className="mb-0">{formatearMoneda(totalesResumen.valor)}</h3>
                                        <p className="text-muted mb-0 small">Valor total</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card card-outline card-success">
                                    <div className="card-body">
                                        <h3 className="mb-0">{totalesResumen.electronicas}</h3>
                                        <p className="text-muted mb-0 small">Electrónicas</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card card-outline card-warning">
                                    <div className="card-body">
                                        <h3 className="mb-0">{totalesResumen.noElectronicas}</h3>
                                        <p className="text-muted mb-0 small">No electrónicas</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card report-fin-card">
                            <div className="card-header">
                                <h3 className="card-title">Resultados ({notas.length} notas)</h3>
                            </div>
                            <div className="card-body p-0">
                                {loading ? (
                                    <div className="text-center py-4"><div className="spinner-border" role="status"><span className="sr-only">Cargando...</span></div></div>
                                ) : (
                                    <div className="report-fin-table-wrap report-fin-table-wrap--fit">
                                        <table className="table table-bordered table-hover mb-0 report-fin-table">
                                            <thead>
                                                <tr>
                                                    <th>N° NC</th>
                                                    <th className="text-center">Tipo</th>
                                                    <th>Fecha</th>
                                                    <th>Factura</th>
                                                    <th>Paciente</th>
                                                    <th>Usuario</th>
                                                    <th className="text-right">Valor total</th>
                                                    <th className="text-center">Docs</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {notas.length > 0 ? pagination.paginatedItems.map((n) => (
                                                    <tr key={`${n.tipo}-${n.id}`}>
                                                        <td><strong>{n.numero_completo || n.numero_nc}</strong></td>
                                                        <td className="text-center">
                                                            <span className={`badge badge-${n.tipo === 'electronica' ? 'success' : 'secondary'}`}>
                                                                {getTipoTexto(n.tipo)}
                                                            </span>
                                                        </td>
                                                        <td>{formatDateOnly(n.fecha_emision)}</td>
                                                        <td>
                                                            {n.factura_id ? (
                                                                <Link to={`/informacion-factura/${n.factura_id}`}>
                                                                    {n.factura_numero_completo || n.numero_factura || n.factura_id}
                                                                </Link>
                                                            ) : (n.factura_numero_completo || 'N/A')}
                                                        </td>
                                                        <td>
                                                            <div>{n.paciente_nombre || 'N/A'}</div>
                                                            {n.paciente_documento && (
                                                                <small className="text-muted">{n.paciente_documento}</small>
                                                            )}
                                                        </td>
                                                        <td>{getNombreUsuario(n)}</td>
                                                        <td className="text-right"><strong>{formatearMoneda(n.valor_total)}</strong></td>
                                                        <td className="text-center">
                                                            {n.pdf_url && (
                                                                <a href={n.pdf_url} target="_blank" rel="noopener noreferrer" className="btn btn-xs btn-outline-danger mr-1" title="PDF DIAN">
                                                                    <i className="fas fa-file-pdf"></i>
                                                                </a>
                                                            )}
                                                            {n.xml_url && (
                                                                <a href={n.xml_url} target="_blank" rel="noopener noreferrer" className="btn btn-xs btn-outline-secondary" title="XML">
                                                                    <i className="fas fa-file-code"></i>
                                                                </a>
                                                            )}
                                                            {!n.pdf_url && !n.xml_url && <span className="text-muted">—</span>}
                                                        </td>
                                                    </tr>
                                                )) : (
                                                    <tr>
                                                        <td colSpan="8" className="text-center py-4 text-muted">No se encontraron notas crédito</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                {!loading && (
                                    <ReportPaginationFooter
                                        totalRegistros={pagination.totalRegistros}
                                        indicePrimerRegistro={pagination.indicePrimerRegistro}
                                        indiceUltimoRegistro={pagination.indiceUltimoRegistro}
                                        paginaActual={pagination.paginaActual}
                                        cambiarPagina={pagination.cambiarPagina}
                                        totalPaginas={pagination.totalPaginas}
                                        registrosPorPagina={pagination.registrosPorPagina}
                                        onPageSizeChange={pagination.handlePageSizeChange}
                                        itemLabel="notas"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
            <Footer showToast={false} delay={0} />
        </div>
    );
}

export default ReporteNotasCredito;
